import styled from '@emotion/styled';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useField } from 'formik';
import { ICustomTheme } from 'interfaces';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Control = styled(FormControl)<{
  theme: Theme;
}>`
  label {
    font-size: 1.4rem;
    margin-bottom: 100px;
  }
  input {
    font-size: 1.6rem;
  }
`;
type Props = {
  helperText?: string;
  label: string;
  name: string;
  type: string;
  variant?: 'standard' | 'outlined' | 'filled' | undefined;
};

export const StyledTextField = styled(TextField)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  // input label when focused
  '& label.Mui-focused': {
    color: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='standard'
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='filled'
  '& .MuiFilledInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='outlined'
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.paletteFromBO.main,
    },
  },
  // disabled state
  '& .MuiInputBase-root.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

export const InputField = (props: Props) => {
  const [field] = useField(props);
  const { label, type, helperText, variant } = props;
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === 'password';
  const resolvedType = isPasswordField && showPassword ? 'text' : type;

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Control>
      <StyledTextField
        id={field.name}
        type={resolvedType}
        variant={variant}
        label={label}
        aria-describedby={field.name}
        {...field}
        InputProps={
          isPasswordField
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword
                          ? t('auth.global.hide_password')
                          : t('auth.global.show_password')
                      }
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="large" />
                      ) : (
                        <Visibility fontSize="large" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
      {helperText && (
        <FormHelperText id={field.name}>{helperText}</FormHelperText>
      )}
    </Control>
  );
};
InputField.defaultProps = {
  required: false,
};

export default InputField;
